import { useContext } from "react";
import { AuthContext } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Package, Users, FileText, Download, LogOut, Plus, AlertTriangle, Database, Upload, Image } from "lucide-react";

export default function DashboardPage() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const stats = [
    {
      title: "Artikel verwalten",
      description: "Artikel anzeigen, bearbeiten und exportieren",
      icon: Package,
      action: () => navigate("/articles"),
      color: "bg-blue-500",
    },
    {
      title: "Downloads",
      description: "Ihre Batch-Downloads anzeigen und herunterladen",
      icon: Download,
      action: () => navigate("/downloads"),
      color: "bg-teal-500",
    },
    {
      title: "Fehler überprüfen",
      description: "Artikel mit fehlenden Informationen finden",
      icon: AlertTriangle,
      action: () => navigate("/validation"),
      color: "bg-orange-500",
    },
  ];

  if (user?.role === "admin") {
    stats.push(
      {
        title: "Kategorien",
        description: "Kategorien und Unterkategorien verwalten",
        icon: FileText,
        action: () => navigate("/categories"),
        color: "bg-purple-500",
      },
      {
        title: "Benutzerverwaltung",
        description: "Benutzer erstellen und verwalten",
        icon: Users,
        action: () => navigate("/users"),
        color: "bg-emerald-500",
      },
      {
        title: "Datenbank Backup",
        description: "Datenbank sichern und wiederherstellen",
        icon: Database,
        action: () => navigate("/backup"),
        color: "bg-slate-600",
      },
      {
        title: "CSV Import",
        description: "Artikel aus CSV-Datei importieren",
        icon: Upload,
        action: () => navigate("/import"),
        color: "bg-indigo-600",
      },
      {
        title: "Media Verwaltung",
        description: "Bilder, Videos, PDFs und Dokumente verwalten",
        icon: Image,
        action: () => navigate("/media"),
        color: "bg-pink-500",
      }
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Artikelverwaltung</h1>
                <p className="text-sm text-slate-600">Willkommen, {user?.name}</p>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              data-testid="logout-button"
              className="flex items-center gap-2 border-slate-300 hover:bg-slate-100"
            >
              <LogOut className="w-4 h-4" />
              Abmelden
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Dashboard</h2>
          <p className="text-slate-600">Verwalten Sie Ihre Artikel und Benutzer</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm card-hover cursor-pointer animate-fade-in"
                onClick={stat.action}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{stat.title}</h3>
                <p className="text-slate-600">{stat.description}</p>
              </div>
            );
          })}
        </div>

        {user?.role === "admin" && (
          <div className="mt-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">Neuen Artikel erstellen</h3>
                <p className="text-blue-100">Fügen Sie einen neuen Artikel zu Ihrer Sammlung hinzu</p>
              </div>
              <Button
                onClick={() => navigate("/articles/new")}
                data-testid="create-article-button"
                className="bg-white text-blue-600 hover:bg-blue-50 font-medium h-11 px-6 rounded-lg shadow-md"
              >
                <Plus className="w-5 h-5 mr-2" />
                Artikel erstellen
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
